import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Layers, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

function todayIso() {
  const d = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function RotaPresetsDialog({ open, onOpenChange, onApplied }) {
  const [presets, setPresets] = useState([]);
  const [pid, setPid] = useState(null);
  const [startDate, setStartDate] = useState(todayIso());
  const [cycles, setCycles] = useState(2);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    http.get("/shifts/presets").then((r) => setPresets(r.data)).catch(() => setPresets([]));
  }, [open]);

  const apply = async () => {
    if (!pid) return;
    setBusy(true);
    try {
      const { data } = await http.post("/shifts/preset", { preset_id: pid, start_date: startDate, cycles: Number(cycles) });
      toast.success("Rota created", { description: `${data.count} shifts added` });
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply preset");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F141F] border-white/10 text-slate-100 sm:max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-300" /> Rota Presets
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            One-tap templates for common shift patterns. Pick one, choose a start date and cycle count.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
          {presets.map((p) => (
            <button
              key={p.id}
              onClick={() => setPid(p.id)}
              data-testid={`preset-${p.id}`}
              className={`text-left rounded-2xl border p-4 transition ${
                pid === p.id
                  ? "border-indigo-400 bg-indigo-500/10"
                  : "border-white/10 bg-slate-900/50 hover:border-indigo-400/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="pill text-[9px] bg-slate-950/70 border-white/10 text-slate-300">{p.role}</span>
                <span className="text-[10px] font-mono-ui text-slate-500 uppercase tracking-widest">{p.days_per_cycle}d cycle</span>
              </div>
              <div className="font-display text-base font-semibold mt-1.5">{p.label}</div>
              <div className="text-xs text-slate-400 mt-1 leading-relaxed">{p.description}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Start date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="preset-start-date"
              className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Cycles</Label>
            <Input
              type="number"
              min={1}
              max={8}
              value={cycles}
              onChange={(e) => setCycles(e.target.value)}
              data-testid="preset-cycles"
              className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full bg-transparent border-white/15 text-slate-200">
            Cancel
          </Button>
          <Button
            onClick={apply}
            disabled={!pid || busy}
            data-testid="preset-apply-btn"
            className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
          >
            <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Applying…" : "Apply preset"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
